import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Download, Search, X, ShieldAlert, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "@/lib/brand-tokens";
import { auditEvents, getAdminById, getMemberById } from "@/lib/mock-data";

// ADM-11 — Audit log
const ENTITY_TYPES = ["all", "Admin", "Member", "Application", "Deal", "Reservation", "Submission", "Card", "PaymentRun"];
const ACTOR_TYPES = [
  { id: "all", label: "All actors" },
  { id: "admin", label: "Admins" },
  { id: "member", label: "Members" },
  { id: "system", label: "System" },
];

export default function AuditLogPage() {
  const [actorType, setActorType] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [decryptOnly, setDecryptOnly] = useState(false);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const allActions = useMemo(() => [...new Set(auditEvents.map((e) => e.action))].sort(), []);

  const filtered = useMemo(() => {
    return auditEvents.filter((e) => {
      const actorIsAdmin  = e.actorId && e.actorId.startsWith("adm_");
      const actorIsMember = e.actorId && e.actorId.startsWith("mbr_");
      if (actorType === "admin"  && !actorIsAdmin)  return false;
      if (actorType === "member" && !actorIsMember) return false;
      if (actorType === "system" && (actorIsAdmin || actorIsMember)) return false;
      if (entityType !== "all" && e.entityType !== entityType) return false;
      if (actionFilter !== "all" && e.action !== actionFilter) return false;
      if (decryptOnly && !e.isDecryption) return false;
      if (q && !`${e.description || ""} ${e.entityId || ""} ${e.action || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [actorType, entityType, actionFilter, decryptOnly, q]);

  const decryptCount = auditEvents.filter((e) => e.isDecryption).length;
  const adminActionCount = auditEvents.filter((e) => e.actorId && e.actorId.startsWith("adm_")).length;

  const clearFilters = () => {
    setActorType("all"); setEntityType("all"); setActionFilter("all"); setDecryptOnly(false); setQ("");
  };
  const activeFilterCount = (actorType !== "all" ? 1 : 0) + (entityType !== "all" ? 1 : 0) + (actionFilter !== "all" ? 1 : 0) + (decryptOnly ? 1 : 0) + (q ? 1 : 0);

  return (
    <div data-testid="audit-log-page" className="space-y-5 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Audit log</h1>
          <p className="text-body-sm text-ink-muted mt-1">
            {filtered.length} of {auditEvents.length} events · {decryptCount} decryption events · {adminActionCount} admin actions
          </p>
        </div>
        <Button variant="outline"><Download className="size-4 mr-1.5" /> Export filtered as CSV</Button>
      </div>

      {/* Filter bar */}
      <div className="border-y border-line bg-surface -mx-8 px-8 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search summary, action, or entity ID…" className="pl-8 h-9 bg-surface-alt border-transparent focus-visible:bg-surface focus-visible:border-line" />
          </div>
          <Select value={actorType} onValueChange={setActorType}>
            <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTOR_TYPES.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {ENTITY_TYPES.filter((e) => e !== "all").map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[200px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All actions</SelectItem>
              {allActions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => setDecryptOnly((s) => !s)} data-testid="decrypt-only-toggle"
                  className={`px-3 h-9 rounded-md border text-caption font-medium inline-flex items-center gap-1.5 transition-all ${
                    decryptOnly ? "bg-orange-50 border-orange-300 text-orange-700" : "bg-surface border-line text-ink-muted hover:border-ink"
                  }`}>
            <ShieldAlert className="size-3.5" />Decryption events {decryptOnly ? `(${decryptCount})` : ""}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="ml-auto text-caption font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
              Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {/* Events table — full bleed */}
      <div className="-mx-8 bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt border-b border-line">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint w-7"></th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Timestamp</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Actor</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Action</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Entity</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Summary</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-8 py-12 text-center text-body-sm text-ink-muted">No events match these filters.</td></tr>
              ) : filtered.slice(0, 80).map((e) => {
                const isExp = expanded[e.id];
                const actor = e.actorId && e.actorId.startsWith("adm_") ? getAdminById(e.actorId)
                            : e.actorId && e.actorId.startsWith("mbr_") ? getMemberById(e.actorId)
                            : null;
                return (
                  <React.Fragment key={e.id}>
                    <tr className={`hover:bg-surface-alt/40 ${e.isDecryption ? "bg-orange-50/30" : ""}`}>
                      <td className="px-8 py-3">
                        <button onClick={() => setExpanded((p) => ({ ...p, [e.id]: !isExp }))}
                                className="size-6 rounded grid place-items-center text-ink-hint hover:bg-surface-alt hover:text-ink">
                          {isExp ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-ink-muted text-caption tabular-nums whitespace-nowrap">
                        <div className="text-ink">{format.date(e.at)}</div>
                        <div className="text-ink-subtle">{format.relative(e.at)}</div>
                      </td>
                      <td className="px-3 py-3">
                        <ActorChip actorId={e.actorId} actor={actor} />
                      </td>
                      <td className="px-3 py-3 font-mono text-code">
                        {e.isDecryption && <ShieldAlert className="size-3.5 inline text-orange-600 mr-1" />}
                        <span className={e.isDecryption ? "text-orange-700 font-semibold" : "text-ink-muted"}>{e.action}</span>
                      </td>
                      <td className="px-3 py-3 font-mono text-code text-ink-subtle">
                        <div>{e.entityType}</div>
                        <div className="text-ink-hint">{e.entityId}</div>
                      </td>
                      <td className="px-3 py-3 text-ink max-w-[400px] truncate">{e.description}</td>
                      <td className="px-8 py-3 font-mono text-code text-ink-subtle whitespace-nowrap">{e.ipAddress}</td>
                    </tr>
                    {isExp && (
                      <tr className={e.isDecryption ? "bg-orange-50/40" : "bg-surface-alt/40"}>
                        <td colSpan={7} className="px-8 py-4">
                          <pre className="font-mono text-code text-ink-muted leading-relaxed whitespace-pre-wrap">
{JSON.stringify({
  event_id: e.id,
  at: e.at,
  actor: { id: e.actorId || "system", ...(actor && { display: `${actor.firstName} ${actor.lastName}`, role: actor.role || actor.email }) },
  action: e.action,
  entity: { type: e.entityType, id: e.entityId, label: e.entityLabel },
  ip: e.ipAddress,
  description: e.description,
  is_decryption: e.isDecryption,
  ...(e.details && { details: e.details }),
}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 80 && (
          <div className="px-8 py-3 border-t border-line text-caption text-ink-subtle text-center">
            Showing first 80 of {filtered.length} matching events. Refine filters to narrow.
          </div>
        )}
      </div>
    </div>
  );
}

function ActorChip({ actorId, actor }) {
  if (actorId && actorId.startsWith("adm_") && actor) {
    return (
      <div className="flex items-center gap-2">
        <span className="size-6 rounded-full bg-ink text-white grid place-items-center text-[10px] font-bold">A</span>
        <div className="min-w-0">
          <div className="text-ink font-medium">{actor.firstName} {actor.lastName?.[0]}.</div>
          <div className="text-caption text-ink-hint truncate">{actor.role}</div>
        </div>
      </div>
    );
  }
  if (actorId && actorId.startsWith("mbr_")) {
    return (
      <div className="flex items-center gap-2">
        <span className="size-6 rounded-full bg-brand-50 text-brand-700 grid place-items-center text-[10px] font-bold">M</span>
        <div className="min-w-0">
          <div className="text-ink font-medium">{actor ? `${actor.firstName} ${actor.lastName?.[0]}.` : "Member"}</div>
          <div className="text-caption text-ink-hint font-mono">{actorId}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="size-6 rounded-full bg-slate-200 text-slate-600 grid place-items-center text-[10px] font-bold">S</span>
      <span className="text-ink-muted">System</span>
    </div>
  );
}
